import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, CardHeader, Container, Grid, MenuItem, TextField, Typography } from "@mui/material";
import MaterialTable from "material-table";
import Swal from "sweetalert2";

import { Icons } from "../utils/Icon";
import { CustomStyles } from "../utils/Styled";
import { useAuth } from "../hooks/useAuth";
import { GetTokenIdentity } from "../utils/Token";
import axios from "axios";

const FIXED_YEARS = 5;

export const ContinuousActiveCustomersAnalysisPage = () => {
    const classes = CustomStyles;
    const navigate = useNavigate();
    const { state } = useLocation();
    const { token, logout } = useAuth();
    const identity = GetTokenIdentity(token);

    const defaultCountryId = Number(state?.countryId) || Number(identity?.user?.country_id);
    const currentYear = new Date().getFullYear();
    const defaultFromYear = currentYear;

    const [countryId] = useState(defaultCountryId);
    const [fromYear, setFromYear] = useState(defaultFromYear);
    const [toYear, setToYear] = useState(currentYear);
    const [firstTransactionYear, setFirstTransactionYear] = useState(currentYear);
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState([]);

    const getValidFirstTransactionYear = (value) => {
        const parsedYear = Number(value);
        return Number.isInteger(parsedYear) && parsedYear > 0 && parsedYear <= currentYear
            ? parsedYear
            : null;
    };

    const yearOptions = useMemo(() => {
        const startYear = Number.isInteger(firstTransactionYear)
            ? Math.min(firstTransactionYear, currentYear)
            : currentYear;

        return Array.from(
            { length: currentYear - startYear + 1 },
            (_, index) => currentYear - index
        );
    }, [currentYear, firstTransactionYear]);

    const fetchYearlyContinuousActiveCustomers = useCallback(async (customFromYear, customToYear) => {
        if (!countryId) return;

        const requestedFromYear = Number(customFromYear) || defaultFromYear;
        const requestedToYear = Number(customToYear) || currentYear;

        if (requestedFromYear > requestedToYear) {
            Swal.fire({
                icon: "warning",
                title: "Invalid Period",
                text: "From Year must be less than or equal to To Year.",
                confirmButtonColor: "#fe7320",
            });
            return;
        }

        setIsLoading(true);
        try {
            const response = await axios.get(
                process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v1/customer-dashboard/${countryId}/yearly-continuous-active-customers`,
                {
                    params: {
                        from_year: requestedFromYear,
                        to_year: requestedToYear,
                        years: FIXED_YEARS,
                    },
                }
            );

            const payload = response?.data?.data || {};
            const validFirstTransactionYear = getValidFirstTransactionYear(payload.firstTransactionYear);
            const rows = (payload.yearlyLoyaltyPerformanceBreakdown || []).map((item) => ({
                year: item.year,
                active_end_users_5y: item.activeEndUsers5Y || 0,
                active_dealers_5y: item.activeDealers5Y || 0,
                total_5y_loyal_base: item.total5YLoyalBase || 0,
            }));

            setFirstTransactionYear(validFirstTransactionYear || currentYear);

            const summary = payload.totalSummary || {};
            const summaryRow = {
                year: "Total Summary",
                active_end_users_5y: summary.activeEndUsers5Y || 0,
                active_dealers_5y: summary.activeDealers5Y || 0,
                total_5y_loyal_base: summary.total5YLoyalBase || 0,
                is_total: true,
            };

            setData([...rows, summaryRow]);
        } catch (error) {
            if (error?.response?.status === 401) {
                logout("Your account has been logged out of a session.");
                return;
            }

            setFirstTransactionYear(currentYear);

            const message = error?.response?.data?.message || "Unable to fetch yearly continuous active customers.";
            Swal.fire({
                icon: "error",
                title: "Failed to Load Data",
                text: message,
                confirmButtonColor: "#fe7320",
            });
        } finally {
            setIsLoading(false);
        }
    }, [countryId, currentYear, defaultFromYear, logout]);

    useEffect(() => {
        fetchYearlyContinuousActiveCustomers(defaultFromYear, currentYear);
    }, [currentYear, countryId, defaultFromYear, fetchYearlyContinuousActiveCustomers]);

    useEffect(() => {
        if (yearOptions.length === 0) return;

        if (!yearOptions.includes(Number(fromYear))) {
            setFromYear(yearOptions[0]);
        }

        if (!yearOptions.includes(Number(toYear))) {
            setToYear(yearOptions[0]);
        }
    }, [fromYear, toYear, yearOptions]);

    const columns = [
        { title: "Year", field: "year" },
        { title: "Active End Users (5Y)", field: "active_end_users_5y" },
        { title: "Active Dealers (5Y)", field: "active_dealers_5y" },
        { title: "Total 5Y Loyal Base", field: "total_5y_loyal_base" },
        {
            title: "Actions",
            field: "action",
            sorting: false,
            render: (rowData) =>
                rowData.is_total ? (
                    ""
                ) : (
                    <Button
                        size="small"
                        color="primary"
                        variant="text"
                        sx={{ minWidth: "auto", px: 0.5 }}
                        onClick={() =>
                            navigate("/organizations/continuous-active-customers-analysis/list", {
                                state: {
                                    countryId,
                                    year: rowData.year,
                                    years: FIXED_YEARS,
                                    periodFromYear: rowData.year - 4,
                                    periodToYear: rowData.year,
                                    totalActive: rowData.total_5y_loyal_base,
                                    endUsers: rowData.active_end_users_5y,
                                    dealers: rowData.active_dealers_5y,
                                },
                            })
                        }
                    >
                        View List
                    </Button>
                ),
        },
    ];

    return (
        <div>
            <div className={classes.background} />
            <Container maxWidth="xl" sx={{ py: 2 }} style={{ padding: 14 }}>
                <MaterialTable
                    title="5-Year Continuous Active Analysis"
                    icons={Icons}
                    columns={columns}
                    data={data}
                    isLoading={isLoading}
                    style={{ padding: "24px" }}
                    options={{
                        search: false,
                        filtering: false,
                        sorting: false,
                        paging: false,
                        toolbar: true,
                        actionsColumnIndex: -1,
                        headerStyle: {
                            fontWeight: "bold",
                            backgroundColor: "#f1f1f1",
                            color: "#212122",
                        },
                        padding: "dense",
                        draggable: false,
                        rowStyle: (rowData) => ({
                            fontWeight: rowData.is_total ? 700 : 400,
                            backgroundColor: rowData.is_total ? "#f9fafb" : "#ffffff",
                        }),
                    }}
                    localization={{
                        body: {
                            emptyDataSourceMessage: "No data to display",
                        },
                    }}
                    components={{
                        Toolbar: () => (
                            <>
                                <Grid container spacing={2} sx={{ px: 0.1, pb: 2 }}>
                                    <Grid item xs={12} sm={8}>
                                        <CardHeader
                                            sx={{ p: 0 }}
                                            title={
                                                <Typography component="h1" variant="h5" color="primary" fontWeight="bold">
                                                    5-Year Continuous Active Analysis
                                                </Typography>
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4} sx={{ textAlign: { xs: "left", sm: "right" } }}>
                                        <Button color="error" onClick={() => navigate("/organizations")}>
                                            Back
                                        </Button>
                                    </Grid>
                                </Grid>

                                <Box
                                    sx={{
                                        mb: 2.5,
                                        border: 1,
                                        borderColor: "#d7e1ed",
                                        borderRadius: 1,
                                        px: 2,
                                        py: 1.3,
                                        backgroundColor: "#ffffff",
                                    }}
                                >
                                    <Typography sx={{ fontSize: "0.95rem", fontWeight: 500, color: "#3a4a5a" }}>Overview Summary</Typography>
                                    <Typography sx={{ mt: 0.5, fontSize: "0.9rem", color: "#3a4a5a" }}>
                                        Definition Period: Active sales generated consecutively for {FIXED_YEARS} years prior to/including the selected year
                                    </Typography>
                                    <Typography sx={{ mt: 0.5, fontSize: "0.9rem", color: "#3a4a5a" }}>
                                        Selected Analysis Period: <strong>{fromYear} to {toYear}</strong>
                                    </Typography>
                                </Box>

                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        alignItems: "center",
                                        flexWrap: "wrap",
                                        columnGap: 1.5,
                                        rowGap: 1.2,
                                        pb: 2.5,
                                    }}
                                >
                                    <Box sx={{ flex: "0 0 140px", minWidth: 140 }}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label="From Year"
                                            value={fromYear}
                                            onChange={(event) => setFromYear(Number(event.target.value))}
                                            InputLabelProps={{ shrink: true }}
                                        >
                                            {yearOptions.map((year) => (
                                                <MenuItem key={year} value={year}>
                                                    {year}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Box>
                                    <Box sx={{ flex: "0 0 140px", minWidth: 140 }}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label="To Year"
                                            value={toYear}
                                            onChange={(event) => setToYear(Number(event.target.value))}
                                            InputLabelProps={{ shrink: true }}
                                        >
                                            {yearOptions.map((year) => (
                                                <MenuItem key={year} value={year}>
                                                    {year}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Box>
                                    <Box sx={{ flex: "0 0 96px", minWidth: 96 }}>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            color="primary"
                                            sx={{ height: "40px", whiteSpace: "nowrap" }}
                                            onClick={() => fetchYearlyContinuousActiveCustomers(fromYear, toYear)}
                                            disabled={isLoading || !countryId}
                                        >
                                            Show
                                        </Button>
                                    </Box>
                                </Box>

                                <Typography sx={{ pb: 1.5, color: "#2e3d4f", fontWeight: 500 }}>Yearly Loyalty Performance Breakdown</Typography>
                            </>
                        ),
                    }}
                />
            </Container>
        </div>
    );
};
